import type {
  BookingType,
  ExpenseCategory,
  ExpenseRecord,
  LegacyTripStatus,
  PaymentMode,
  PaymentRecord,
  Trip,
  TripFinancials,
  TripFormData,
  TripRisk,
  TripStatus,
  VehicleSource,
} from "./types";
import {
  BOOKING_TYPES,
  EXPENSE_CATEGORIES,
  PAYMENT_MODES,
  VEHICLE_SOURCES,
} from "./types";

type LegacyTripLike = Partial<Omit<Trip, "status">> & {
  status?: TripStatus | LegacyTripStatus;
};

const LOW_MARGIN_THRESHOLD = 0.12;
const HIGH_EXPENSE_RATIO = 0.75;
const LOW_ADVANCE_RATIO = 0.2;
export const OVERDUE_DAYS = 15;
const TRANSIT_DELAY_DAYS = 3;

export const toNumber = (value: unknown): number => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const toMonthKey = (date: string): string => date.slice(0, 7);
export const toYearKey = (date: string): string => date.slice(0, 4);

const normalizeChoice = <T extends string>(
  value: unknown,
  choices: readonly T[],
  fallback: T
): T => (choices.includes(value as T) ? (value as T) : fallback);

export const normalizeStatus = (
  status?: TripStatus | LegacyTripStatus
): TripStatus => {
  if (status === "Completed") return "Delivered";
  if (!status) return "Planned";
  return status;
};

export const normalizeBookingType = (value?: unknown): BookingType =>
  normalizeChoice(value, BOOKING_TYPES, "Direct");

export const normalizeVehicleSource = (value?: unknown): VehicleSource =>
  normalizeChoice(value, VEHICLE_SOURCES, "Own Vehicle");

export const normalizePaymentMode = (value?: unknown): PaymentMode =>
  normalizeChoice(value, PAYMENT_MODES, "Cash");

export const normalizeExpenseCategory = (value?: unknown): ExpenseCategory =>
  normalizeChoice(value, EXPENSE_CATEGORIES, "Other");

export const normalizeVehicleNo = (value?: unknown): string =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

export const generateTripNo = (date: string, id: string): string => {
  const compactDate = (date || new Date().toISOString().slice(0, 10)).replace(
    /-/g,
    ""
  );
  const suffix = id.replace(/[^a-zA-Z0-9]/g, "").slice(-4).toUpperCase() || "0000";
  return `HL-${compactDate}-${suffix}`;
};

export const calculateTotalExpense = (
  trip: Pick<
    Trip,
    "fuelExp" | "fastagExp" | "driverBata" | "commissionExp" | "otherExp"
  >
): number =>
  toNumber(trip.fuelExp) +
  toNumber(trip.fastagExp) +
  toNumber(trip.driverBata) +
  toNumber(trip.commissionExp) +
  toNumber(trip.otherExp);

export const calculatePaymentTotal = (payments: PaymentRecord[]): number =>
  payments.reduce((sum, payment) => sum + toNumber(payment.amount), 0);

export const calculateExpenseTotal = (expenses: ExpenseRecord[]): number =>
  expenses.reduce((sum, expense) => sum + toNumber(expense.amount), 0);

export const expenseRecordsToScalarFields = (
  expenses: ExpenseRecord[]
): Pick<
  Trip,
  "fuelExp" | "fastagExp" | "driverBata" | "commissionExp" | "otherExp"
> =>
  expenses.reduce(
    (acc, expense) => {
      const amount = toNumber(expense.amount);
      if (expense.category === "Fuel") acc.fuelExp += amount;
      else if (expense.category === "FASTag") acc.fastagExp += amount;
      else if (expense.category === "Driver Bata") acc.driverBata += amount;
      else if (expense.category === "Commission") acc.commissionExp += amount;
      else acc.otherExp += amount;
      return acc;
    },
    {
      fuelExp: 0,
      fastagExp: 0,
      driverBata: 0,
      commissionExp: 0,
      otherExp: 0,
    }
  );

export const calculateFinancials = (
  trip: Pick<
    Trip,
    | "hireAmount"
    | "advanceReceived"
    | "fuelExp"
    | "fastagExp"
    | "driverBata"
    | "commissionExp"
    | "otherExp"
    | "status"
  >
): TripFinancials => {
  const totalExpense = calculateTotalExpense(trip);
  const amountReceived = toNumber(trip.advanceReceived);
  const hireAmount = toNumber(trip.hireAmount);
  const outstanding = Math.max(0, hireAmount - amountReceived);
  const operatingProfit = hireAmount - totalExpense;

  return {
    totalExpense,
    amountReceived,
    outstanding,
    operatingProfit,
    marginPct: hireAmount > 0 ? operatingProfit / hireAmount : 0,
    cashCollectedPct: hireAmount > 0 ? amountReceived / hireAmount : 0,
  };
};

export const buildTripSearchIndex = (
  trip: Pick<
    Trip,
    | "id"
    | "tripNo"
    | "date"
    | "bookingType"
    | "vehicleSource"
    | "brokerName"
    | "vehicleNo"
    | "driverName"
    | "driverMobile"
    | "partyName"
    | "origin"
    | "destination"
    | "status"
    | "notes"
  >
): string =>
  [
    trip.id,
    trip.tripNo,
    trip.date,
    trip.bookingType,
    trip.vehicleSource,
    trip.brokerName,
    trip.vehicleNo,
    trip.driverName,
    trip.driverMobile,
    trip.partyName,
    trip.origin,
    trip.destination,
    trip.status,
    trip.notes,
  ]
    .join(" ")
    .toLowerCase();

export const hydratePaymentRecord = (
  payment: Partial<PaymentRecord>
): PaymentRecord => ({
  id: String(payment.id ?? "").trim(),
  tripId: String(payment.tripId ?? "").trim(),
  date: String(payment.date ?? "").trim(),
  amount: toNumber(payment.amount),
  mode: normalizePaymentMode(payment.mode),
  note: String(payment.note ?? "").trim(),
});

export const hydrateExpenseRecord = (
  expense: Partial<ExpenseRecord>
): ExpenseRecord => ({
  id: String(expense.id ?? "").trim(),
  tripId: String(expense.tripId ?? "").trim(),
  date: String(expense.date ?? "").trim(),
  category: normalizeExpenseCategory(expense.category),
  amount: toNumber(expense.amount),
  note: String(expense.note ?? "").trim(),
});

export const hydrateTrip = (trip: LegacyTripLike): Trip => {
  const status = normalizeStatus(trip.status);
  const date = trip.date ?? "";
  const id = trip.id ?? "";
  const numericTrip = {
    ...trip,
    id,
    tripNo: trip.tripNo || generateTripNo(date, id),
    date,
    bookingType: normalizeBookingType(trip.bookingType),
    vehicleSource: normalizeVehicleSource(trip.vehicleSource),
    brokerName: (trip.brokerName ?? "").trim(),
    vehicleNo: normalizeVehicleNo(trip.vehicleNo),
    driverName: (trip.driverName ?? "").trim(),
    driverMobile: (trip.driverMobile ?? "").trim(),
    partyName: (trip.partyName ?? "").trim(),
    origin: (trip.origin ?? "").trim(),
    destination: (trip.destination ?? "").trim(),
    status,
    hireAmount: toNumber(trip.hireAmount),
    advanceReceived: toNumber(trip.advanceReceived),
    fuelExp: toNumber(trip.fuelExp),
    fastagExp: toNumber(trip.fastagExp),
    driverBata: toNumber(trip.driverBata),
    commissionExp: toNumber(trip.commissionExp),
    otherExp: toNumber(trip.otherExp),
    monthKey: date ? toMonthKey(date) : "",
    notes: (trip.notes ?? "").trim(),
    settledAt: status === "Settled" ? trip.settledAt : undefined,
  };
  const financials = calculateFinancials(numericTrip);
  const hydrated = {
    ...numericTrip,
    totalExpense: financials.totalExpense,
    balance: financials.outstanding,
    profit: financials.operatingProfit,
  };

  return {
    ...hydrated,
    searchIndex: buildTripSearchIndex(hydrated),
  };
};

export const hydrateTripWithRecords = (
  trip: LegacyTripLike,
  payments: PaymentRecord[] = [],
  expenses: ExpenseRecord[] = []
): Trip => {
  const hasPayments = payments.length > 0;
  const hasExpenses = expenses.length > 0;
  const expenseFields = hasExpenses ? expenseRecordsToScalarFields(expenses) : {};

  return hydrateTrip({
    ...trip,
    ...(hasPayments ? { advanceReceived: calculatePaymentTotal(payments) } : {}),
    ...expenseFields,
  });
};

export const buildTripFromForm = (
  form: TripFormData,
  id: string,
  updatedAt = new Date().toISOString()
): Trip => {
  const draft: Trip = {
    id,
    tripNo: form.tripNo.trim() || generateTripNo(form.date, id),
    date: form.date,
    bookingType: normalizeBookingType(form.bookingType),
    vehicleSource: normalizeVehicleSource(form.vehicleSource),
    brokerName: form.brokerName.trim(),
    vehicleNo: normalizeVehicleNo(form.vehicleNo),
    driverName: form.driverName.trim(),
    driverMobile: form.driverMobile.trim(),
    partyName: form.partyName.trim(),
    origin: form.origin.trim(),
    destination: form.destination.trim(),
    hireAmount: toNumber(form.hireAmount),
    advanceReceived: toNumber(form.advanceReceived),
    fuelExp: toNumber(form.fuelExp),
    fastagExp: toNumber(form.fastagExp),
    driverBata: toNumber(form.driverBata),
    commissionExp: toNumber(form.commissionExp),
    otherExp: toNumber(form.otherExp),
    totalExpense: 0,
    balance: 0,
    profit: 0,
    status: form.status,
    monthKey: toMonthKey(form.date),
    notes: form.notes.trim(),
    searchIndex: "",
    settledAt: form.status === "Settled" ? updatedAt : undefined,
    updatedAt,
  };

  return hydrateTrip(draft);
};

export const daysSince = (date: string, today = new Date()): number => {
  if (!date) return 0;
  const start = new Date(`${date}T00:00:00`);
  if (Number.isNaN(start.getTime())) return 0;
  const diff = today.getTime() - start.getTime();
  return Math.max(0, Math.floor(diff / 86_400_000));
};

export const getTripRisks = (trip: Trip, today = new Date()): TripRisk[] => {
  const financials = calculateFinancials(trip);
  const age = daysSince(trip.date, today);
  const risks: TripRisk[] = [];

  if (!trip.vehicleNo) {
    risks.push({
      id: `${trip.id}-missing-vehicle`,
      tripId: trip.id,
      code: "missingVehicle",
      label: "Vehicle missing",
      detail: `${trip.tripNo || trip.id} has no vehicle number`,
      severity: "info",
      moneyImpact: trip.hireAmount,
    });
  }

  if (!trip.partyName) {
    risks.push({
      id: `${trip.id}-missing-party`,
      tripId: trip.id,
      code: "missingParty",
      label: "Party missing",
      detail: `${trip.tripNo || trip.id} has no paying party`,
      severity: "warning",
      moneyImpact: trip.hireAmount,
    });
  }

  if (
    ["Broker", "Indirect"].includes(trip.bookingType) &&
    !trip.brokerName
  ) {
    risks.push({
      id: `${trip.id}-missing-broker`,
      tripId: trip.id,
      code: "missingBroker",
      label: "Broker missing",
      detail: `${trip.tripNo || trip.id} is marked ${trip.bookingType.toLowerCase()}`,
      severity: "warning",
      moneyImpact: trip.hireAmount,
    });
  }

  if (trip.hireAmount <= 0) {
    risks.push({
      id: `${trip.id}-missing-hire`,
      tripId: trip.id,
      code: "missingHire",
      label: "Hire missing",
      detail: "Trip has no hire amount",
      severity: "critical",
      moneyImpact: 0,
    });
  }

  if (financials.totalExpense <= 0 && !["Draft", "Planned", "Cancelled"].includes(trip.status)) {
    risks.push({
      id: `${trip.id}-missing-expense`,
      tripId: trip.id,
      code: "missingExpense",
      label: "Expenses missing",
      detail: `${trip.tripNo || trip.id} has no direct expense captured`,
      severity: "info",
      moneyImpact: trip.hireAmount,
    });
  }

  if (
    financials.outstanding > 0 &&
    ["Delivered", "Billed", "Partially Settled"].includes(trip.status) &&
    age > OVERDUE_DAYS
  ) {
    risks.push({
      id: `${trip.id}-overdue`,
      tripId: trip.id,
      code: "overdue",
      label: "Collection overdue",
      detail: `${age} days open for ${trip.partyName}`,
      severity: "critical",
      moneyImpact: financials.outstanding,
    });
  }

  if (trip.hireAmount > 0 && financials.marginPct < LOW_MARGIN_THRESHOLD) {
    risks.push({
      id: `${trip.id}-margin`,
      tripId: trip.id,
      code: "lowMargin",
      label: "Low margin",
      detail: `${Math.round(financials.marginPct * 100)}% margin on ${trip.vehicleNo}`,
      severity: "warning",
      moneyImpact: Math.abs(financials.operatingProfit),
    });
  }

  if (trip.hireAmount > 0 && financials.totalExpense / trip.hireAmount > HIGH_EXPENSE_RATIO) {
    risks.push({
      id: `${trip.id}-expense`,
      tripId: trip.id,
      code: "expensePressure",
      label: "Expense pressure",
      detail: "Direct cost is above 75% of hire",
      severity: "warning",
      moneyImpact: financials.totalExpense,
    });
  }

  if (
    trip.hireAmount > 0 &&
    financials.cashCollectedPct < LOW_ADVANCE_RATIO &&
    trip.status !== "Settled"
  ) {
    risks.push({
      id: `${trip.id}-advance`,
      tripId: trip.id,
      code: "lowAdvance",
      label: "Low advance",
      detail: "Less than 20% received against hire",
      severity: "info",
      moneyImpact: financials.outstanding,
    });
  }

  if (trip.status === "In Transit" && age > TRANSIT_DELAY_DAYS) {
    risks.push({
      id: `${trip.id}-transit-delay`,
      tripId: trip.id,
      code: "transitDelay",
      label: "Transit delay",
      detail: `${age} days since dispatch date`,
      severity: "warning",
      moneyImpact: trip.hireAmount,
    });
  }

  return risks;
};
